import React, { useState, useMemo } from "react";
import axios from "axios";
import {
  Search, Building2, Globe, MapPin, Linkedin, CheckCircle2, Plus, Loader2, X,
  ExternalLink, AlertTriangle, Database, TrendingUp, AlertOctagon
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import ApolloStyleFilters from "../../components/ApolloStyleFilters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── Result Card ──────────────────────────────────────────────────────────
function ResultCard({ org, onSave, savingId, savedIds }) {
  const isSaved = savedIds.has(org.id);
  const isSaving = savingId === org.id;
  return (
    <div data-testid="company-result-card" className="group border border-white/[0.04] bg-[#0D0F12] hover:border-[#00E5FF]/40 transition-all duration-300 overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-[#00E5FF] to-transparent" />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          {org.logo_url ? (
            <img src={org.logo_url} alt={org.name} className="w-12 h-12 object-contain border border-white/[0.04] bg-white p-1 flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 flex items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex-shrink-0">
              <Building2 className="w-5 h-5 text-[#00E5FF]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white leading-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }} title={org.name}>{org.name}</h3>
            {org.domain && (
              <a href={`https://${org.domain}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-[#00E5FF] hover:text-white transition-colors mt-0.5">
                <Globe className="w-3 h-3" /> {org.domain}
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
            )}
          </div>
        </div>

        {org.industry && (
          <div className="mb-3">
            <span className="inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF] font-semibold">
              {org.industry}
            </span>
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          <div className="border border-white/[0.04] bg-[#050505] p-2 text-center">
            <p className="text-[8px] uppercase tracking-widest text-[#A0AEC0]">Dipendenti</p>
            <p className="text-sm font-bold tabular-nums text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              {org.estimated_num_employees ? org.estimated_num_employees.toLocaleString("it-IT") : "—"}
            </p>
          </div>
          <div className="border border-white/[0.04] bg-[#050505] p-2 text-center">
            <p className="text-[8px] uppercase tracking-widest text-[#A0AEC0]">Fatturato</p>
            <p className="text-sm font-bold tabular-nums text-[#16C784] truncate" style={{ fontFamily: "Chivo, sans-serif" }} title={org.annual_revenue_printed}>
              {org.annual_revenue_printed || "—"}
            </p>
          </div>
          <div className="border border-white/[0.04] bg-[#050505] p-2 text-center">
            <p className="text-[8px] uppercase tracking-widest text-[#A0AEC0]">Fondata</p>
            <p className="text-sm font-bold tabular-nums text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              {org.founded_year || "—"}
            </p>
          </div>
        </div>

        {(org.hq_city || org.hq_country) && (
          <div className="flex items-center gap-1.5 text-xs text-[#CBD5E1] mb-3">
            <MapPin className="w-3 h-3 text-[#A0AEC0] flex-shrink-0" />
            <span>{[org.hq_city, org.hq_country].filter(Boolean).join(", ")}</span>
          </div>
        )}

        {org.keywords && org.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {org.keywords.slice(0, 6).map((k) => (
              <span key={k} className="text-[9px] px-1.5 py-0.5 bg-[#0A0C10] border border-white/[0.04] text-[#CBD5E1] tracking-wide">{k}</span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-white/[0.04]">
          {org.linkedin_url && (
            <a href={org.linkedin_url} target="_blank" rel="noopener noreferrer"
               className="flex-shrink-0 flex items-center gap-1 px-2 py-2 border border-white/[0.04] bg-[#050505] hover:border-[#00E5FF]/40 hover:text-[#00E5FF] text-[#CBD5E1] transition-colors">
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
          <a
            href={`/entity-loss-simulation?name=${encodeURIComponent(org.name)}`}
            data-testid={`company-simulate-loss-${org.id}`}
            title="Simula perdita entità"
            className="flex-shrink-0 flex items-center gap-1 px-2 py-2 border border-[#FF1744]/40 bg-[#FF1744]/8 hover:bg-[#FF1744]/15 text-[#FF1744] transition-colors"
          >
            <AlertOctagon className="w-3.5 h-3.5" />
          </a>
          <button
            data-testid={`company-save-${org.id}`}
            onClick={() => onSave(org)}
            disabled={isSaved || isSaving}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-[11px] font-bold tracking-widest uppercase transition-all ${
              isSaved
                ? "bg-[#16C784]/15 border border-[#16C784]/50 text-[#16C784]"
                : "bg-[#00E5FF] text-black hover:bg-white disabled:opacity-60"
            }`}
          >
            {isSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Aggiungo…</>
             : isSaved ? <><CheckCircle2 className="w-3.5 h-3.5" /> Aggiunto</>
             : <><Plus className="w-3.5 h-3.5" /> Aggiungi al database</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
export default function BusinessIDCard() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [toast, setToast] = useState(null);

  // Client-side filter over Apollo results (free-tier doesn't support facet search)
  const filteredResults = useMemo(() => {
    const empBucket = (n) => {
      if (!n) return null;
      if (n <= 10)   return "1-10";
      if (n <= 50)   return "11-50";
      if (n <= 200)  return "51-200";
      if (n <= 1000) return "201-1000";
      if (n <= 5000) return "1001-5000";
      return "5000+";
    };
    const revBucket = (s) => {
      if (!s || typeof s !== "string") return null;
      const cleaned = s.replace(/[€$\s]/g, "");
      const m = cleaned.match(/([\d.,]+)\s*([KMB])?/i);
      if (!m) return null;
      const num = parseFloat(m[1].replace(",", "."));
      const mult = { K: 1e3, M: 1e6, B: 1e9 }[(m[2] || "").toUpperCase()] || 1;
      const r = num * mult;
      if (r < 1e6)         return "0-1M";
      if (r < 10e6)        return "1M-10M";
      if (r < 50e6)        return "10M-50M";
      if (r < 250e6)       return "50M-250M";
      if (r < 1e9)         return "250M-1B";
      return "1B+";
    };
    return results.filter(o => {
      if (filters.industry && !(o.industry || "").toLowerCase().includes(filters.industry.toLowerCase())) return false;
      if (filters.employees && empBucket(o.estimated_num_employees) !== filters.employees) return false;
      if (filters.revenue   && revBucket(o.annual_revenue_printed) !== filters.revenue) return false;
      if (filters.location) {
        const loc = filters.location.toLowerCase();
        if (![o.hq_city, o.hq_country].some(v => (v || "").toLowerCase().includes(loc))) return false;
      }
      if (filters.verified && (o.verification_status || "").toLowerCase() !== "verified") return false;
      if (filters.has_website && !o.domain) return false;
      if (filters.has_tech_stack && !(o.keywords && o.keywords.length > 0)) return false;
      return true;
    });
  }, [results, filters]);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (q.length < 2) { setError("Inserisci almeno 2 caratteri"); return; }
    setLoading(true); setError(""); setResults([]); setTotal(0);
    try {
      const { data } = await axios.post(`${API}/apollo/search`, { query: q }, { withCredentials: true });
      setResults(data.results || []);
      setTotal(data.total || 0);
      if ((data.results || []).length === 0) {
        setError("Nessuna azienda trovata. Prova con un dominio (es. stripe.com) o un nome esatto.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante la ricerca");
    } finally { setLoading(false); }
  };

  const handleSave = async (org) => {
    setSavingId(org.id);
    try {
      const { data } = await axios.post(`${API}/apollo/save`, { data: org }, { withCredentials: true });
      setSavedIds(prev => new Set([...prev, org.id]));
      setToast({
        type: "success",
        title: data.already_existed ? "Aggiornata" : "Aggiunta al database",
        msg: `${org.name} è ora disponibile in Business List. Aprila per consultare la scheda completa, KYC compliance e download bilancio.`,
      });
      setTimeout(() => setToast(null), 6000);
    } catch (err) {
      setToast({ type: "error", title: "Errore", msg: err.response?.data?.detail || "Impossibile salvare l'azienda" });
      setTimeout(() => setToast(null), 5000);
    } finally { setSavingId(null); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      {/* Hero header */}
      <div>
        <div className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-bold">
          <Database className="w-3 h-3" /> Intelligence · Company Check
        </div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Company Check</h1>
        <p className="text-sm text-[#A0AEC0] mt-1 max-w-2xl">
          Cerca un'azienda a livello globale, verifica dati aziendali, tecnologie, contatti chiave e revenue. Con un click aggiungila alla <span className="text-[#00E5FF] font-semibold">Business List</span> del tuo tenant.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={doSearch} className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
          <input
            data-testid="company-search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca un'azienda per nome o dominio... (es. Stripe, OmniSteel, danieli.com)"
            className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#6B7280]"
            autoFocus
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="submit"
          data-testid="company-search-button"
          disabled={loading}
          className="bg-[#00E5FF] text-black px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cerco…</> : <><Search className="w-3.5 h-3.5" /> Cerca azienda</>}
        </button>
      </form>

      {/* Apollo-style header filters (collapsible) */}
      <ApolloStyleFilters
        entity="company"
        filters={filters}
        setFilters={setFilters}
        onApply={() => { /* client-side filter is instant on results */ }}
      />

      {/* Error */}
      {error && (
        <div data-testid="company-error" className="border border-[#F5B82E]/40 bg-[#F5B82E]/10 px-4 py-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-[#F5B82E] flex-shrink-0" />
          <span className="text-[#F5B82E]">{error}</span>
        </div>
      )}

      {/* Results header */}
      {results.length > 0 && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#6B7280] pt-2">
          <span>
            <span className="text-white font-bold tabular-nums">{filteredResults.length}</span>
            {filteredResults.length !== results.length && <span> / <span className="text-white font-bold tabular-nums">{results.length}</span></span>}
            {total > results.length && <span> di <span className="text-white font-bold tabular-nums">{total.toLocaleString("it-IT")}</span></span>} corrispondenze
          </span>
          <span className="flex items-center gap-1.5 text-[#00E5FF]">
            <TrendingUp className="w-2.5 h-2.5" /> Business Intelligence Network
          </span>
        </div>
      )}

      {/* Empty state (no search yet) */}
      {!loading && !error && results.length === 0 && (
        <div className="border border-dashed border-white/[0.04] bg-[#0D0F12] py-16 flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex items-center justify-center mb-4">
            <Database className="w-7 h-7 text-[#00E5FF]" />
          </div>
          <p className="text-white font-semibold mb-1">Cerca un'azienda per iniziare</p>
          <p className="text-xs text-[#A0AEC0] max-w-md mb-4">
            Digita il nome o il dominio nella barra sopra. Riceverai una scheda arricchita con dati aziendali, tecnologie e KPI. Aggiungila alla Business List per accedere a bilancio e KYC compliance.
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="border border-white/[0.04] bg-[#0D0F12] p-5">
              <div className="flex gap-3 mb-4">
                <div className="w-12 h-12 bg-[#1F242D] animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#1F242D] animate-pulse w-3/4" />
                  <div className="h-2 bg-[#1F242D] animate-pulse w-1/2" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <div className="h-10 bg-[#1F242D] animate-pulse" />
                <div className="h-10 bg-[#1F242D] animate-pulse" />
                <div className="h-10 bg-[#1F242D] animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results grid */}
      {!loading && results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredResults.length === 0 ? (
            <div className="col-span-full border border-dashed border-white/[0.04] bg-[#0D0F12] py-10 text-center">
              <AlertTriangle className="w-6 h-6 text-[#F5B82E] mx-auto mb-2" />
              <p className="text-white text-sm font-semibold">Nessun risultato dopo i filtri</p>
              <p className="text-xs text-[#A0AEC0] mt-1">Prova ad allentare uno o più filtri Apollo-style.</p>
            </div>
          ) : filteredResults.map((org, i) => (
            <ResultCard key={org.id || i} org={org} onSave={handleSave} savingId={savingId} savedIds={savedIds} />
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="company-toast"
             className="fixed bottom-6 right-6 max-w-sm border p-4 shadow-2xl z-50"
             style={{
               borderColor: toast.type === "success" ? "#059669" : "#DC2626",
               background: toast.type === "success" ? "#0D0F12" : "#1A0B0B",
               boxShadow: `0 20px 60px -20px ${toast.type === "success" ? "#16C78455" : "#FF3B3055"}`,
             }}>
          <div className="flex items-start gap-3">
            {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 text-[#16C784] flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />}
            <div className="flex-1">
              <p className="text-sm font-bold text-white mb-0.5">{toast.title}</p>
              <p className="text-xs text-[#CBD5E1] leading-relaxed">{toast.msg}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-[#A0AEC0] hover:text-white flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
